/**
 * Workflow comment hooks — back the Flow Designer's Comments tab.
 *
 * Thin React Query wrappers over `/workflows/:id/comments`, modeled on the card
 * comment hooks (`features/cards/hooks/useCards.ts`). The list is newest-first;
 * posting a comment invalidates the list so the thread refreshes in place.
 *
 * @module FlowDesigner/hooks/useWorkflowComments
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type WorkflowComment = {
  id: string;
  workflowId: string;
  authorId: string;
  body: string;
  nodeId: string | null;
  createdAt: string;
  updatedAt: string;
};

const commentsKey = (workflowId: string) => ['workflow', 'comments', workflowId] as const;

export function useWorkflowComments(workflowId: string | null | undefined) {
  return useQuery({
    queryKey: commentsKey(workflowId ?? ''),
    enabled: !!workflowId,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/workflows/${workflowId}/comments`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to load comments');
      const data = (await r.json()) as { ok: true; data: WorkflowComment[] };
      return data.data;
    },
  });
}

export function useCreateWorkflowComment(workflowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, nodeId }: { body: string; nodeId?: string | null }) => {
      const r = await fetch(`${API_URL}/workflows/${workflowId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(nodeId ? { body, nodeId } : { body }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to post comment');
      const data = (await r.json()) as { ok: true; data: WorkflowComment };
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: commentsKey(workflowId) });
    },
  });
}
